import { keepPreviousData, useQueryClient } from '@tanstack/react-query';

import { useApiGet, useApiSend } from '../../shared/api/utils/reactQueryProvider';
import {
  deleteIncident,
  getActions,
  getAllAssignees,
  getAllSafetyIncidents,
  getClassifications,
  getOffenderTypes,
  getSafetyIncident,
  getStatuses,
  getSubClassifications,
  getTripRelations,
  patchIncident,
  postAdminNote,
  postIncident,
} from '../api/services/Incidents';
import { AddIncidentFormValues } from '../features/IncidentForm/AddIncidentForm';
import { EditIncidentFormValues } from '../features/IncidentForm/EditIncidentForm/';
import {
  Incident,
  IncidentAction,
  IncidentClassification,
  IncidentIndividual,
  IncidentNote,
  IncidentNotePayload,
  IncidentOffenderType,
  IncidentStatus,
  IncidentSubclassification,
  IncidentTrip,
} from '../types/incidents';

export const useGetAllSafetyIncidents = (query) => {
  return useApiGet<{
    meta: {
      page: number;
      perPage: number;
      totalCount: number;
    };
    data: Incident[];
  }>(['getAllSafetyIncidents', query], () => getAllSafetyIncidents(query), {
    enabled: !!query.page,
    placeholderData: keepPreviousData,
  });
};

export const useGetSafetyIncident = (id: number) => {
  return useApiGet<Incident>(['getSafetyIncident', id], () => getSafetyIncident(id), {
    enabled: !!id && id >= 0,
  });
};

export const useGetTripRelations = (id: string | number | null) => {
  return useApiGet<IncidentTrip>(['getTripRelations', id], () => getTripRelations(id), {
    retry: false,
    enabled: () => {
      if (!!id) {
        return (typeof id === 'string' && /^[a-f0-9]{14}$/.test(id)) || (typeof id === 'number' && id >= 0);
      }

      return false;
    },
  });
};

export const useGetOffenderTypes = () => {
  return useApiGet<{ data: IncidentOffenderType[] }>(['getOffenderTypes'], getOffenderTypes, {});
};

export const useGetStatuses = () => {
  return useApiGet<{ data: IncidentStatus[] }>(['getStatuses'], getStatuses, {});
};

export const useGetActions = () => {
  return useApiGet<{ data: IncidentAction[] }>(['getIncidentActions'], getActions, {});
};

export const useGetClassifications = (query, options = {}) => {
  return useApiGet<{
    meta: {
      page: number;
      perPage: number;
      totalCount: number;
    };
    data: IncidentClassification[];
  }>(['getIncidentClassifications', query], () => getClassifications(query), {
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const useGetSubClassifications = (query, options = {}) => {
  return useApiGet<{
    meta: {
      page: number;
      perPage: number;
      totalCount: number;
    };
    data: IncidentSubclassification[];
  }>(['getIncidentSubClassifications', query], () => getSubClassifications(query), {
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const usePostAdminNote = (onSuccess?: (data) => void, onError?: (error) => void) => {
  const queryClient = useQueryClient();

  return useApiSend(
    async (payload: IncidentNotePayload) => postAdminNote(payload.id, payload.content) as Promise<IncidentNote>,
    (data: IncidentNote) => {
      queryClient.invalidateQueries({ queryKey: ['getSafetyIncident', data.id] });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError,
    ['postAdminNote'],
    {}
  );
};

export const usePostIncident = (onSuccess?: (data) => void, onError?: (error) => void) => {
  const queryClient = useQueryClient();

  return useApiSend(
    (payload: { data: AddIncidentFormValues }) => postIncident(payload.data) as Promise<Incident>,
    (data: Incident) => {
      queryClient.invalidateQueries({ queryKey: ['getAllSafetyIncidents'] });
      queryClient.invalidateQueries({ queryKey: ['getSafetyIncident', data.id] });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError,
    ['postIncident'],
    {}
  );
};

export const usePatchIncident = (onSuccess?: (data) => void, onError?: (error) => void) => {
  const queryClient = useQueryClient();

  return useApiSend(
    (payload: { id: number; data: EditIncidentFormValues }) =>
      patchIncident(payload.id, payload.data) as Promise<Incident>,
    (data: Incident) => {
      queryClient.invalidateQueries({ queryKey: ['getAllSafetyIncidents'] });
      queryClient.invalidateQueries({ queryKey: ['getSafetyIncident', data.id] });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError,
    ['patchIncident'],
    {}
  );
};

export const useDeleteIncident = (onSuccess?: (data) => void, onError?: (error) => void) => {
  const queryClient = useQueryClient();

  return useApiSend(
    (payload: { id: number }) => deleteIncident(payload.id),
    (data) => {
      queryClient.invalidateQueries({ queryKey: ['getAllSafetyIncidents'] });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError,
    ['deleteIncident'],
    {}
  );
};

// TODO: Maybe move these to another file, since it is not in the Safety Incidents namespace
export const useGetAllAssignees = (query) => {
  return useApiGet<{
    meta: {
      page: number;
      perPage: number;
      totalCount: number;
    };
    data: IncidentIndividual[];
  }>(['getAllAssignees', query], () => getAllAssignees(query), {
    placeholderData: keepPreviousData,
  });
};
